import { useEffect, useRef, useState, type ReactElement } from "react";
import type {
  ProblemTimelineParams,
  TimelineMilestone,
  TimelineMilestoneKind,
} from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import { ExposurePanel } from "./ExposurePanel.js";
import { MilestoneList } from "./MilestoneList.js";
import { MilestoneDetails } from "./MilestoneDetails.js";
import { TimelineSummaryStrip } from "./TimelineSummary.js";
import { TimelineTrack } from "./TimelineTrack.js";
import {
  isoDate,
  moveMilestone,
  reorderMilestones,
  timelineSummary,
} from "./presentation.js";

const NEW_LABEL: Partial<Record<TimelineMilestoneKind, string>> = {
  occurred: "Problem occurred",
  recognised: "First recognised",
};

let fallbackMilestoneSequence = 0;

function nextMilestoneId(kind: TimelineMilestoneKind): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return `milestone-${kind}-${crypto.randomUUID()}`;
  }
  fallbackMilestoneSequence += 1;
  return `milestone-${kind}-${Date.now().toString(36)}-${fallbackMilestoneSequence.toString(36)}`;
}

export function ProblemTimelineEditor({
  data: incomingData,
  onChange,
  readOnly = false,
}: WidgetBodyProps<ProblemTimelineParams>): ReactElement {
  const [data, setData] = useState(incomingData);
  const [selectedId, setSelectedId] = useState<string | undefined>(
    incomingData.milestones[0]?.id,
  );
  const dataRef = useRef(incomingData);
  const pendingRef = useRef<ProblemTimelineParams | null>(null);
  useEffect(() => {
    if (
      pendingRef.current &&
      JSON.stringify(incomingData) !== JSON.stringify(pendingRef.current)
    ) {
      return;
    }
    pendingRef.current = null;
    dataRef.current = incomingData;
    setData(incomingData);
  }, [incomingData]);
  const commit = (
    update: (current: ProblemTimelineParams) => ProblemTimelineParams,
  ): void => {
    if (readOnly) return;
    const next = update(dataRef.current);
    dataRef.current = next;
    pendingRef.current = next;
    setData(next);
    onChange(next);
  };

  const summary = timelineSummary(data);
  const selected =
    data.milestones.find((item) => item.id === selectedId) ??
    data.milestones[0];

  const patchMilestone = (
    id: string,
    patch: Partial<TimelineMilestone>,
  ): void =>
    commit((current) => ({
      ...current,
      milestones: current.milestones.map((milestone) =>
        milestone.id === id ? { ...milestone, ...patch } : milestone,
      ),
    }));

  const addMilestone = (kind: TimelineMilestoneKind): void => {
    if (readOnly) return;
    const id = nextMilestoneId(kind);
    setSelectedId(id);
    commit((current) => ({
      ...current,
      milestones: [
        ...current.milestones,
        {
          id,
          kind,
          label: NEW_LABEL[kind] ?? "New milestone",
          date: isoDate(Date.now()),
        },
      ],
    }));
  };

  const deleteMilestone = (id: string): void =>
    commit((current) => ({
      ...current,
      milestones: current.milestones.filter((milestone) => milestone.id !== id),
    }));

  // Both reorder paths (pointer drag and the Move up/down buttons) persist the
  // same way: a new `milestones` array. Nothing else in the params changes.
  const reorder = (orderedIds: string[]): void =>
    commit((current) => ({
      ...current,
      milestones: reorderMilestones(current.milestones, orderedIds),
    }));

  const move = (id: string, delta: number): void => {
    const next = moveMilestone(dataRef.current.milestones, id, delta);
    if (next === dataRef.current.milestones) return;
    commit((current) => ({ ...current, milestones: [...next] }));
  };

  const setOptionalNumber = (
    key: "exposureRatePerDay" | "estimatedExposure",
    raw: string,
  ): void => {
    if (raw.trim() === "") {
      commit((current) => {
        const { [key]: _removed, ...rest } = current;
        void _removed;
        return rest;
      });
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    commit((current) => ({ ...current, [key]: Math.max(0, parsed) }));
  };

  const setOptionalText = (
    key: "exposureUnit" | "exposureBasis",
    value: string,
  ): void => {
    if (value === "") {
      commit((current) => {
        const { [key]: _removed, ...rest } = current;
        void _removed;
        return rest;
      });
      return;
    }
    commit((current) => ({ ...current, [key]: value }));
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col gap-3 overflow-y-auto pe-1">
      <TimelineSummaryStrip data={data} variant="plain" />

      <section className="min-w-0 rounded-lg border border-pb-border bg-pb-card-elevated/30 p-3">
        <TimelineTrack
          data={data}
          compact={false}
          readOnly={readOnly}
          selectedId={selected?.id}
          onSelect={setSelectedId}
          onMove={(id, date) => patchMilestone(id, { date })}
        />
        <p className="mt-2 text-dense-1 text-pb-text-muted">
          Select a milestone to see its details. Dates run in chronological
          order; spacing is not to scale.
        </p>
      </section>

      {summary.issues.length > 0 ? (
        <div className="rounded-md border border-pb-red/30 bg-pb-red/10 px-2.5 py-1.5 text-dense-1 text-pb-red-text">
          {summary.issues.map((issue) => (
            <div key={issue}>⚠ {issue}</div>
          ))}
        </div>
      ) : null}

      <div className="grid min-w-0 items-start gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <MilestoneList
          data={data}
          readOnly={readOnly}
          onAdd={addMilestone}
          selectedId={selected?.id}
          onSelect={setSelectedId}
          onReorder={reorder}
        />
        {selected ? (
          <MilestoneDetails
            key={selected.id}
            milestone={selected}
            index={data.milestones.findIndex((item) => item.id === selected.id)}
            count={data.milestones.length}
            readOnly={readOnly}
            onPatch={(patch) => patchMilestone(selected.id, patch)}
            onDelete={() => deleteMilestone(selected.id)}
            onMove={(delta) => move(selected.id, delta)}
          />
        ) : (
          <p className="p-4 text-sm text-pb-text-muted">
            Add a milestone to get started.
          </p>
        )}
      </div>
      <details className="rounded-lg border border-pb-border">
        <summary className="cursor-pointer px-4 py-3 text-sm text-pb-text-secondary focus-ring">
          Exposure details
          <span className="ms-4 text-xs text-pb-text-muted">
            {summary.exposure === null
              ? "Not sized"
              : data.estimatedExposure === undefined
                ? "Calculated from rate × suspect window"
                : "Recorded estimate"}
          </span>
        </summary>
        <div className="p-3 pt-0">
          <ExposurePanel
            data={data}
            readOnly={readOnly}
            onChangeNumber={setOptionalNumber}
            onChangeText={setOptionalText}
          />
        </div>
      </details>
    </div>
  );
}
